import { FaqAccordionProps, FaqAccordion as FaqAccordionUI } from "@business-finland/wif-ui-lib";
import { useRouter } from "next/router";
import { useMemo } from "react";
import Head from "next/head";

import { jcrMultiFieldToArray } from "../../../util/magnolia";
import useImageDataHandler from "../../../hooks/useImageDataHandler";
import { generateFaqSchema } from "../../../service/schemaOrg/generators/faq";
import type { IFaqAccordionProps } from "./types";
import HtmlParser from "@/components/HtmlParser";

export function FaqAccordion(props: IFaqAccordionProps): JSX.Element | null {
  const { locale } = useRouter();
  const imageDataHandler = useImageDataHandler();
  
  const items = useMemo(
    () =>
      jcrMultiFieldToArray(props.items).map((item) => ({
        id: item["@id"],
        heading: item.heading,
        body: item.body,
      })),
    [props.items]
  );

  if (items.length === 0) return null;

  const imageData = props.image ? imageDataHandler(props.image) : undefined;

  // Generate schema only for FAQ mode
  const schema =
    props.displayMode !== "accordion"
      ? generateFaqSchema(items, locale || "en")
      : null;

  const itemsProcessed: FaqAccordionProps["items"] = items.map((item) => ({
    ...item,
    body: <HtmlParser html={item.body} />,
  }));

  return (
    <>
      {schema && (
        <Head>
          <script
            type="application/ld+json"
            nonce={process.env.NEXT_PUBLIC_SCRIPT_NONCE}
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        </Head>
      )}
      <FaqAccordionUI
        id={props.anchorTag}
        title={props.title}
        introText={props.introText}
        image={imageData}
        imageAlt={props.imageAlt}
        items={itemsProcessed}
        mode={props.displayMode}
        defaultOpen={props.defaultOpen}
      />
    </>
  );
}

